import {
  Box,
  Divider,
  LuxToggle,
  LuxTypography,
  Stack,
} from '@gemini/fe-ds-core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { usePatientStore } from '@store/patientStore';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import {
  ComponentProps,
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  MarketingConsentField,
  MarketingConsentFieldType,
  MarketingConsentFormProps,
  MarketingConsentFormValues,
} from './MarketingConsentForm.interface';
import { marketingConsentFormSchema } from './MarketingConsentForm.schema';
import {
  getMarketingConsentDefaultValues,
  getMarketingConsentFields,
  getMarketingConsentLabel,
  getMarketingConsentMessage,
  hasVisibleMarketingConsentFields,
} from './MarketingConsentForm.utils';

type FormBuilderLuxToggleProps = ComponentProps<typeof LuxToggle> & {
  error?: boolean;
  noHelper?: boolean;
  noLabel?: boolean;
};

const FormBuilderLuxToggle =
  LuxToggle as ComponentType<FormBuilderLuxToggleProps>;

const marketingConsentLabelBlockSelector = [
  '& label .MuiTypography-root > p',
  '& label .MuiTypography-root > div',
  '& label > p',
  '& label > div',
].join(', ');

const marketingConsentToggleSx = {
  '& label .MuiTypography-root': {
    display: 'inline',
  },
  [marketingConsentLabelBlockSelector]: {
    display: 'inline',
    margin: 0,
  },
};

const MarketingConsentForm = ({ onValidate }: MarketingConsentFormProps) => {
  const [t] = useTranslation();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);
  const setShouldSave = useOnlineBookingStore(state => state.setShouldSave);
  const contactsDetails = usePatientStore(state => state.contactsDetails);
  const updateContactDetails = usePatientStore(
    state => state.updateContactDetails,
  );

  const fields = useMemo(
    () =>
      getMarketingConsentFields(
        formConfigs?.customFieldsMarketing,
        integrationMode,
      ),
    [formConfigs?.customFieldsMarketing, integrationMode],
  );

  const visibleFields = useMemo(
    () =>
      fields.filter(
        field => field.config.status !== StaticValues.FIELD_STATUS_HIDDEN,
      ),
    [fields],
  );

  const initialDefaultValues = useMemo(
    () => getMarketingConsentDefaultValues(fields, contactsDetails),
    [contactsDetails, fields],
  );

  const validationMode = brandName ? 'onBlur' : 'onChange';

  const {
    control,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<MarketingConsentFormValues>({
    mode: validationMode,
    reValidateMode: validationMode,
    resolver: yupResolver(marketingConsentFormSchema(fields)),
    defaultValues: initialDefaultValues,
  });

  const persistMarketingValues = useCallback(
    (storeCustomFieldsMarketing: Record<string, boolean | undefined>) => {
      const currentContactsDetails = usePatientStore.getState().contactsDetails;
      const nextMarketingValues = {
        ...currentContactsDetails.storeCustomFieldsMarketing,
        ...storeCustomFieldsMarketing,
      };
      const currentKeys = Object.keys(
        currentContactsDetails.storeCustomFieldsMarketing ?? {},
      );
      const nextKeys = Object.keys(nextMarketingValues);
      const hasSameValues =
        currentKeys.length === nextKeys.length &&
        nextKeys.every(
          key =>
            currentContactsDetails.storeCustomFieldsMarketing?.[key] ===
            nextMarketingValues[key],
        );

      if (hasSameValues) {
        return;
      }

      updateContactDetails({
        ...currentContactsDetails,
        storeCustomFieldsMarketing: nextMarketingValues,
      });
      setShouldSave(true);
    },
    [setShouldSave, updateContactDetails],
  );

  const handleConsentChange = useCallback(
    (field: MarketingConsentField, checked: boolean) => {
      const currentMarketingValues =
        getValues('contactsDetails.storeCustomFieldsMarketing') ?? {};
      const nextMarketingValues = {
        ...currentMarketingValues,
        [field.valueKey]: checked,
        ...(field.id ? { [field.id]: checked } : {}),
      };

      setValue(
        'contactsDetails.storeCustomFieldsMarketing',
        nextMarketingValues,
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        },
      );
      persistMarketingValues(nextMarketingValues);
    },
    [getValues, persistMarketingValues, setValue],
  );

  useEffect(() => {
    const defaultValues = getMarketingConsentDefaultValues(
      fields,
      usePatientStore.getState().contactsDetails,
    );

    reset(defaultValues);
    persistMarketingValues(
      defaultValues.contactsDetails.storeCustomFieldsMarketing,
    );
    void trigger();
  }, [fields, persistMarketingValues, reset, trigger]);

  useEffect(() => {
    onValidate(!hasVisibleMarketingConsentFields(fields) || isValid);
  }, [fields, isValid, onValidate]);

  if (!hasVisibleMarketingConsentFields(fields)) {
    return null;
  }

  return (
    <Box width="100%">
      <Stack sx={{ padding: '0 1rem', width: '100%' }}>
        <Divider />
      </Stack>
      <Box
        display="grid"
        width="100%"
        gridTemplateColumns="repeat(12, 1fr)"
        columnGap="1rem"
        rowGap="1rem"
        padding="0.5rem 1rem 1rem 1rem"
      >
        {visibleFields.map(field => {
          if (field.type === MarketingConsentFieldType.MESSAGE) {
            return (
              <Box key={field.valueKey} gridColumn="span 12 / span 12">
                <LuxTypography>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getMarketingConsentMessage(field, !!brandName, t),
                    }}
                  />
                </LuxTypography>
              </Box>
            );
          }

          return (
            <Controller
              key={field.valueKey}
              name={`contactsDetails.storeCustomFieldsMarketing.${field.valueKey}`}
              control={control}
              render={({ field: controllerField, fieldState }) => {
                return (
                  <Box gridColumn="span 12 / span 12">
                    <FormBuilderLuxToggle
                      {...controllerField}
                      label={getMarketingConsentLabel(field, !!brandName, t)}
                      size="medium"
                      disabled={false}
                      labelIsHtml
                      sx={marketingConsentToggleSx}
                      checked={!!controllerField.value}
                      error={fieldState.invalid}
                      required={
                        field.config.status ===
                        StaticValues.FIELD_STATUS_MANDATORY
                      }
                      helperText={fieldState.error?.message}
                      noHelper={false}
                      noLabel={false}
                      onBlur={controllerField.onBlur}
                      onChange={event => {
                        const checked = event.target.checked;

                        controllerField.onChange(checked);
                        handleConsentChange(field, checked);
                      }}
                    />
                  </Box>
                );
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default MarketingConsentForm;
